/** 转换云排版的数据到彩标 */
import { fetchEzSortToken } from '@/apis/colorLabel'
import { IBigPlank, PartType } from '@/partTypes'
import router from '@/router'
import store from '@/store'
import { Modal, message } from 'ant-design-vue'
import fileSaver from 'file-saver'
import { xorBy } from 'lodash-es'

import { countSourceData } from '../createTag'

type PushItem = {
  plank: PartType
  /** 标签在列表中的序号，主标与副标 partUniqueId 相同，需与 createTag 的 label_num 一致 */
  label_num: number
  _filename: string
  _page: number
  datasources: string[][]
}

interface IResult {
  /** 平台 */
  platform: string
  /** 文件生成时间 */
  createdAt: number
  /** 需要处理的文件所在位置 */
  inputFilePath: string
  /** 需要处理的文件类型 */
  fileType: 'pdf' | 'img'
  /** 每个待处理文件的标签数据 */
  tagDataList: {
    _filename: string
    _page: number
    datasources: string[][]
  }[]
  /** 需要解析的字段 */
  parseKeys: {
    filenameKey: '_filename'
    pageKey: '_page'
  }
  datasource: Record<string, string>[]
  [key: string]: any
}

/**
 * @description 转换云排版的数据到彩标
 * @param pattern 模型类型
 * @returns
 */
class Transform {
  private dataList: PushItem[] = []
  /** 解析模式 */
  private pattern: 'pdf' | 'img'
  /** 页码 */
  private page = 0
  /** 可使用的数据项列表 */
  private labelList: typeof defaultDatasourcesTagArr = []
  /** 需要处理的文件名称 */
  private filePath = ''
  /** 最后生成彩标解析的 json 文件名称 */
  protected jsonPath = ''
  /** 彩标token */
  private ezSortToken = ''
  /** 是否强制不使用浏览器自动写入(标签页面的下载 pdf，，设置了自动写入也是直接下载动浏览器自己的位置的) */
  private isForceNotUseAutoWrite = false
  constructor() {
    this.pattern = 'img'
    this.labelList = defaultDatasourcesTagArr
  }
  /** 记录页码，因为云排版标签生成的特性，需要在不同的代码函数中分别的执行并记录所需要数据产生的页码
   *  需要保证 pdf 中生成的顺序是和页面上对的上的，此数据主要就是针对 pdf
   */
  public addPage() {
    this.page++
  }
  public setPage(page: number) {
    this.page = page
  }
  public setPattern(pattern: 'pdf' | 'img') {
    this.pattern = pattern
  }
  /** 设置最终生成的文件名称 */
  public setFilePath(name: string) {
    this.filePath = name
  }
  /** 设置彩标token */
  private setEzSortToken(token: string) {
    this.ezSortToken = token
  }
  public setIsForceNotUseAutoWrite(v: boolean) {
    this.isForceNotUseAutoWrite = v
  }
  public push(item: PushItem) {
    if (window.webToQt) return
    this.dataList.push(item)
  }
  /**
   * @description 生成额外的数据项，由于部分数据在生成标签时已经生成过了，所以此处传入标签模板，可以过滤出部分数据
   * @param tempData 标签模板数据 不传或空数组则会全部生成一次数据项信息 | 不影响正常标签代码
   * @param options 执行countSourceData所需参数
   */
  public generateExtraDatasources(
    tempData: any[] = [],
    options: {
      part: PartType
      plank: IBigPlank
      plank_index: number
      total_part: number
      template: any
      label_num: number
    }
  ) {
    this.labelList = defaultDatasourcesTagArr.concat(
      store.state.thinkerxMaterialKeys.map((item) => ({
        ...item,
        sample: item.text,
      }))
    )
    /** 获取数据源 */
    const datasources = xorBy(
      tempData
        .filter((v) => v.type === 'DataSource')
        .map((v) => ({
          source_data: v.data.source_data,
          sizeRestrictNum: v.data.sizeRestrictNum,
        })),
      this.labelList.map((v) => ({
        source_data: v.value,
        sizeRestrictNum: 2,
      })),
      'source_data'
    )
    const { part, plank, plank_index, total_part, template, label_num } =
      options
    const result = datasources.map((v) => [
      v.source_data,
      countSourceData(
        part,
        plank,
        plank_index,
        total_part,
        template,
        null,
        v,
        label_num,
        { genTagWay: 'export' }
      ),
    ])
    const target = this.findPushItem(part, label_num)
    this.appendEzSortRecognizableFields(result, part)
    if (target) {
      target.datasources = result
    } else {
      this.push({
        plank: part,
        label_num,
        _filename: '',
        _page: this.page,
        datasources: result,
      })
    }
  }

  private findPushItem(part: PartType, labelNum: number) {
    return this.dataList.find(
      (v) =>
        v.plank.partUniqueId === part.partUniqueId && v.label_num === labelNum
    )
  }

  private appendEzSortRecognizableFields(result: string[][], part: PartType) {
    const { longSide: oRectLongSide, shortSide: oRectShortSide } =
      this.getSortedSides(part.oRect.width, part.oRect.height)
    const { longSide: realRectLongSide, shortSide: realRectShortSide } =
      this.getSortedSides(part.realRect.width, part.realRect.height)

    // 一些特殊的字段添加，非标签上的，但是彩标可以识别
    this.upsertDatasource(result, 'oRectWidth', String(part.oRect.width))
    this.upsertDatasource(result, 'oRectHeight', String(part.oRect.height))
    this.upsertDatasource(result, 'realRectWidth', String(part.realRect.width))
    this.upsertDatasource(
      result,
      'realRectHeight',
      String(part.realRect.height)
    )
    this.upsertDatasource(result, 'thick', String(part.thick))

    // 长边短边：对宽高排序后取值
    this.upsertDatasource(result, 'oRectLongSide', String(oRectLongSide))
    this.upsertDatasource(result, 'oRectShortSide', String(oRectShortSide))
    this.upsertDatasource(result, 'realRectLongSide', String(realRectLongSide))
    this.upsertDatasource(
      result,
      'realRectShortSide',
      String(realRectShortSide)
    )
  }

  private getSortedSides(width: number, height: number) {
    const longSide = Math.max(width, height)
    const shortSide = Math.min(width, height)
    return { longSide, shortSide }
  }

  private upsertDatasource(result: string[][], key: string, value: string) {
    const index = result.findIndex((item) => item[0] === key)
    if (index > -1) {
      result[index] = [key, value]
      return
    }
    result.push([key, value])
  }
  /**
   * @description 图片的名称是后续生成的，通过此方法来同步彩标可以得到的图片名称 | 不影响正常标签代码
   * @param plank
   * @param fileName
   */
  public changeImageFileName(
    plank: PartType,
    fileName: string,
    suffix?: string,
    labelNum?: number
  ) {
    const target =
      labelNum !== undefined
        ? this.findPushItem(plank, labelNum)
        : this.dataList.find(
            (v) => v.plank.partUniqueId === plank.partUniqueId
          )
    if (target) {
      target._filename = `${fileName}.${suffix ?? 'jpg'}`
    }
  }
  private download() {
    const data = this.transform()
    const timestamp = new Date().toLocaleString().replace(/[\/: ]+/g, '_')
    this.jsonPath = `ezsort_${this.filePath}_${timestamp}`
    fileSaver.saveAs(
      new Blob([JSON.stringify(data)], {
        type: 'application/json',
      }),
      `${this.jsonPath}.json`
    )
  }
  private transform() {
    // 删除 plank 属性，因为彩标不需要
    const dataList = this.dataList.map((v) => {
      const nv = { ...v }
      Reflect.deleteProperty(nv, 'plank')
      Reflect.deleteProperty(nv, 'label_num')
      return nv
    })
    const result: IResult = {
      platform: 'bluen',
      createdAt: new Date().getTime(),
      inputFilePath: this.filePath,
      fileType: this.pattern,
      tagDataList: dataList,
      parseKeys: {
        filenameKey: '_filename',
        pageKey: '_page',
      },
      bluen: {
        isAutoUncompress:
          store.state.preferencesSetting?.setting?.is_use_folder_export &&
          !this.isForceNotUseAutoWrite,
      },
      datasource: this.labelList.map((item) => ({
        label: item.label,
        sample: item.sample,
        value: item.value,
      })),
    }
    // 每次都重置
    this.setIsForceNotUseAutoWrite(false)
    return result
  }
  /** 下载彩标必要数据，并打开彩标 */
  open() {
    /**
     * path - 彩标解析的 json 文件路径
     * storePath - 文件下载所在位置，浏览器一般是用户下载目录下，但是其他软件或者文件解压写入功能可能需要传不同的
     */
    const openSrc = `ezsort://open?path=${this.jsonPath}.json&storePath=downloads&token=${this.ezSortToken}`
    // 打开一个新的页面，并且携带参数,需要完整路径
    window.open(
      `${router.resolve('/jumpEzSort').href}?openSrc=${encodeURIComponent(
        openSrc
      )}`,
      '_blank'
    )
  }
  /** 处理 */
  async processDownloadOpenEzsort(path: string) {
    try {
      // 不兼容内嵌
      if (window.webToQt) return
      const res = await fetchEzSortToken()
      if (res.status !== 1) {
        return Modal.warning({
          title: '提示',
          content: res.msg,
          centered: true,
        })
      }
      this.setFilePath(path)
      this.setEzSortToken(res.data.token)
      this.download()
      this.open()
    } catch (error) {
      console.log('error', error)
      return message.error('出现未知错误')
    }
  }
  public clear() {
    this.dataList = []
    this.page = 0
  }
}
/** 默认存在的一组标签数据项模板，如果出现了网络问题什么的，就使用这个----复制于 2025-05-27 */
const defaultDatasourcesTagArr = [
  {
    id: 1,
    label: '项目地址',
    sample: '北京市东城区东长安街',
    value: 'address',
    is_update: 0,
  },
  {
    id: 2,
    label: '订单号',
    sample: '0001-01',
    value: 'orderNo',
    is_update: 0,
  },
  {
    id: 3,
    label: '客户',
    sample: '张某某1',
    value: 'customer_name',
    is_update: 0,
  },
  {
    id: 4,
    label: '设计师',
    sample: '李设计',
    value: 'designer',
    is_update: 0,
  },
  {
    id: 5,
    label: '房间',
    sample: '主卧',
    value: 'roomName',
    is_update: 0,
  },
  {
    id: 6,
    label: '柜体',
    sample: '衣柜',
    value: 'loc',
    is_update: 0,
  },
  {
    id: 7,
    label: '板件名',
    sample: '横隔板01',
    value: 'partName',
    is_update: 0,
  },
  {
    id: 8,
    label: '板件成品尺寸(长x宽x厚)',
    sample: '2440X1220X18',
    value: 'oSize',
    is_update: 0,
  },
  {
    id: 9,
    label: '板件成品尺寸(仅长宽)',
    sample: '1220X2440',
    value: 'oSize_L',
    is_update: 0,
  },
  {
    id: 10,
    label: '板件材质与颜色',
    sample: '多层实木-T01',
    value: 'matCode_texture',
    is_update: 0,
  },
  {
    id: 11,
    label: '侧孔信息',
    sample: '1←2↓3→4↑',
    value: 'sholeInfo',
    is_update: 0,
  },
  {
    id: 12,
    label: '侧孔信息(翻面)',
    sample: '1←2↓3→4↑',
    value: 'sholeInfoF',
    is_update: 0,
  },
  {
    id: 13,
    label: '侧槽信息',
    sample: '1←2↓3→4↑',
    value: 'sslotInfo',
    is_update: 0,
  },
  {
    id: 14,
    label: '侧槽信息(翻面)',
    sample: '1←2↓3→4↑',
    value: 'sslotInfoF',
    is_update: 0,
  },
  {
    id: 15,
    label: '小板序号',
    sample: '11#',
    value: 'plankID',
    is_update: 0,
  },
  {
    id: 16,
    label: '大板序号',
    sample: '1',
    value: 'bigPlankID',
    is_update: 0,
  },
  {
    id: 17,
    label: '第几张大板',
    sample: '第1 张大板',
    value: 'plank_index',
    is_update: 0,
  },
  {
    id: 18,
    label: '标签页码',
    sample: '第1/10页',
    value: 'lable_index',
    is_update: 0,
  },
  {
    id: 19,
    label: '拉手名称',
    sample: '单孔拉手',
    value: 'handle_name',
    is_update: 0,
  },
  {
    id: 20,
    label: '拉直器名称',
    sample: '默认拉直器',
    value: 'straightener_label',
    is_update: 0,
  },
  {
    id: 21,
    label: '铰链名称',
    sample: '默认铰链',
    value: 'hinge_label',
    is_update: 0,
  },
  {
    id: 22,
    label: '切割尺寸',
    sample: '200*200*18',
    value: 'cutSize',
    is_update: 0,
  },
  {
    id: 23,
    label: '安装编号',
    sample: '10',
    value: 'installNum',
    is_update: 0,
  },
  {
    id: 24,
    label: '备注信息',
    sample: '备注信息',
    value: 'remarks',
    is_update: 0,
  },
  {
    id: 25,
    label: '正面加工标注',
    sample: '加工正面',
    value: 'frontProcess',
    is_update: 0,
  },
  {
    id: 26,
    label: '反面加工标注',
    sample: '加工反面',
    value: 'reverseProcess',
    is_update: 0,
  },
  {
    id: 27,
    label: '侧面加工标注',
    sample: '加工侧面',
    value: 'sideProcess',
    is_update: 0,
  },
  {
    id: 28,
    label: '排版时间',
    sample: '2022/01/01 00:00:00',
    value: 'paibanTime',
    is_update: 0,
  },
  {
    id: 29,
    label: '板件标注',
    sample: '板件标注',
    value: 'plankSign',
    is_update: 0,
  },
  {
    id: 30,
    label: '包数',
    sample: '1',
    value: 'packageCount',
    is_update: 0,
  },
  {
    id: 31,
    label: '板件条码',
    sample: '6160479583801',
    value: 'oriPlankNum',
    is_update: 0,
  },
  {
    id: 32,
    label: '铰链规格',
    sample: '内嵌式_默认铰链',
    value: 'partsChildren',
    is_update: 0,
  },
  {
    id: 33,
    label: '大板尺寸',
    sample: '2440*1220',
    value: 'plankSize',
    is_update: 0,
  },
  {
    id: 267,
    label: '1',
    sample: '1',
    value: '1',
    is_update: 1,
  },
  {
    id: 269,
    label: '3',
    sample: '3',
    value: '3',
    is_update: 1,
  },
  {
    id: 271,
    label: '5',
    sample: '5',
    value: '5',
    is_update: 1,
  },
  {
    id: 272,
    label: '6',
    sample: '6',
    value: '6',
    is_update: 1,
  },
  {
    id: 273,
    label: '7',
    sample: '7',
    value: '7',
    is_update: 1,
  },
  {
    id: 274,
    label: '4',
    sample: '4',
    value: '4',
    is_update: 1,
  },
  {
    id: 275,
    label: '2',
    sample: '2',
    value: '2',
    is_update: 1,
  },
  {
    id: 279,
    label: '特殊工艺',
    sample: '无',
    value: 'process_label',
    is_update: 0,
  },
  {
    id: 280,
    label: '小板孔槽总数',
    sample: '0孔0槽',
    value: 'hsInfo',
    is_update: 1,
  },
  {
    id: 281,
    label: '铰链孔位置',
    sample: '铰链孔位置',
    value: 'hingeHoleLocation',
    is_update: 1,
  },
  {
    id: 282,
    label: '小板孔槽总数',
    sample: '0孔0槽',
    value: 'hsInfoTotal',
    is_update: 1,
  },
  {
    id: 283,
    label: '铰链孔位置',
    sample: '铰链孔位置',
    value: 'hingeHoleLocationData',
    is_update: 1,
  },
  {
    id: 284,
    label: '板件条码(无标示)',
    sample: '6160479583801',
    value: 'oriPlankNumNoMark',
    is_update: 1,
  },
  {
    id: 289,
    label: '板件成品尺寸(宽x长x厚)',
    sample: '1220X2440X18',
    value: 'oSize_W_L_T',
    is_update: 0,
  },
  {
    id: 290,
    label: '板件备注',
    sample: '板件备注',
    value: 'plank_remarks',
    is_update: 1,
  },
  {
    id: 291,
    label: 'erte',
    sample: 'erte',
    value: 'ytyt',
    is_update: 1,
  },
  {
    id: 292,
    label: '板件材质',
    sample: '多层实木',
    value: 'matCode',
    is_update: 1,
  },
  {
    id: 308,
    label: '板件材质（不含高光字段）',
    sample: '多层实木',
    value: 'matCode_not_high',
    is_update: 1,
  },
  {
    id: 293,
    label: '板件颜色',
    sample: 'T01',
    value: 'texture',
    is_update: 1,
  },
  {
    id: 294,
    label: '铰链位置',
    sample: '铰链位置',
    value: 'hingeInfo',
    is_update: 1,
  },
  {
    id: 296,
    label: '房间备注',
    sample: '房间备注',
    value: 'remark',
    is_update: 1,
  },
  {
    id: 297,
    label: '客户名称(老板良)',
    sample: '张某某',
    value: 'customer',
    is_update: 1,
  },
  {
    id: 298,
    label: '纹理',
    sample: '无纹理',
    value: 'texDir',
    is_update: 1,
  },
  {
    id: 299,
    label: '小板序号（不带#号）',
    sample: '11',
    value: 'onlyPlankID',
    is_update: 1,
  },
  {
    id: 301,
    label: '海棠角',
    sample: '海棠角',
    value: 'handleSlopes',
    is_update: 1,
  },
  {
    id: 302,
    label: '连接方式',
    sample: '板件连接方式',
    value: 'connection_types',
    is_update: 1,
  },
  {
    id: 303,
    label: '对纹组',
    sample: '板件对纹',
    value: 'plankTexAlignName',
    is_update: 1,
  },
  {
    id: 304,
    label: '封边',
    sample: '1(0.3)←1(0.3)↓1(0.3)→1(0.3)↑1(0.3)',
    value: 'edgeInfomation',
    is_update: 1,
  },
  {
    id: 305,
    label: '同尺寸小板数量总计（矩形板）',
    sample: '同尺寸小板数量总计（矩形板）',
    value: 'sameSizeBoardCount',
    is_update: 1,
  },
  {
    id: 306,
    label: '对纹',
    sample: '板件对纹-对纹组名称↑↓←→',
    value: 'plank_tex_refer_planks',
    is_update: 1,
  },
  {
    id: 307,
    label: '柜体尺寸',
    sample: '柜体尺寸',
    value: 'loc_size',
    is_update: 1,
  },
  {
    id: 308,
    label: '面板标记',
    sample: '面板标记',
    value: 'isFrontPlank',
    is_update: 1,
  },
  {
    id: 308,
    label: '特殊标记',
    sample: '特殊标记',
    value: 'specialMark',
    is_update: 1,
  },
  {
    id: 309,
    label: '成品尺寸长',
    sample: '2440',
    value: 'oRectHeight',
    is_update: 1,
  },
  {
    id: 310,
    label: '成品尺寸宽',
    sample: '1220',
    value: 'oRectWidth',
    is_update: 1,
  },
  {
    id: 311,
    label: '板件厚度',
    sample: '18',
    value: 'thick',
    is_update: 1,
  },
  {
    id: 312,
    label: '切割尺寸长',
    sample: '2440',
    value: 'realRectHeight',
    is_update: 1,
  },
  {
    id: 313,
    label: '切割尺寸宽',
    sample: '1220',
    value: 'realRectWidth',
    is_update: 1,
  },
  {
    id: 314,
    label: '板件成品长边',
    sample: '2440',
    value: 'oRectLongSide',
    is_update: 1,
  },
  {
    id: 315,
    label: '板件成品短边',
    sample: '1220',
    value: 'oRectShortSide',
    is_update: 1,
  },
  {
    id: 316,
    label: '板件切割长边',
    sample: '2440',
    value: 'realRectLongSide',
    is_update: 1,
  },
  {
    id: 317,
    label: '板件切割短边',
    sample: '1220',
    value: 'realRectShortSide',
    is_update: 1,
  },
]

export const transformToEzSort = new Transform()
